import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import IconContainer from "@/components/ui/icon-container";
import { ArrowRight, Product } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import {
  TermsItemChange,
  TermsItemStatus
} from "@/pages/contracts/components/updateTerms/types";
import { formatCurrency } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { changeValueText } from "./changeValueText";
import { useTermsVersionChanges } from "./hooks";

interface Props {
  contractId: string;
  versionId: string;
  // The initial terms have nothing before them, so there is no diff to fetch.
  isInitialTerms: boolean;
  currency: string | null;
}

// Total over the statuses that render, so a new backend status is a compile
// error here rather than a badge that silently stops appearing. UNCHANGED is
// excluded because it is filtered out before a row is ever built.
type ChangedStatus = Exclude<TermsItemStatus, "UNCHANGED">;
type ChangedItem = TermsItemChange & { status: ChangedStatus };

// A predicate rather than a bare `!==`, so the narrowed status flows into the
// two lookups below and neither needs a runtime fallback.
const isChangedItem = (item: TermsItemChange): item is ChangedItem =>
  item.status !== "UNCHANGED";

const STATUS_CLASSES: Record<ChangedStatus, string> = {
  ADDED: "bg-badges-positive text-positive",
  MODIFIED: "bg-badges-natural text-natural",
  DELETED: "bg-badges-negative text-error"
};

const STATUS_KEYS: Record<ChangedStatus, string> = {
  ADDED: "contracts.termsHistory.statusAdded",
  MODIFIED: "contracts.termsHistory.statusModified",
  DELETED: "contracts.termsHistory.statusDeleted"
};

// One row per changed field, so the service and its status repeat down the
// Service column rather than sitting in a header above the group — the design
// reads as a table, not as sections.
//
// `fieldIndex` is null for a whole-line row. The API reports no field changes
// for an added or deleted service — "the whole line is the change", in the
// backend's words — so those lines carry an empty `changedFields` and would be
// dropped entirely by a flatMap over it, taking the most common kind of
// amendment out of the table with `hasChanges` still true.
interface ChangeRow {
  key: string;
  item: ChangedItem;
  fieldIndex: number | null;
}

const buildRows = (items: ChangedItem[]): ChangeRow[] =>
  items.flatMap((item, itemIndex): ChangeRow[] => {
    const lineKey = item.revenueItemId ?? `${item.serviceItemId}-${itemIndex}`;
    if (item.changedFields.length === 0) {
      return [{ key: `${lineKey}-whole-line`, item, fieldIndex: null }];
    }
    return item.changedFields.map((change, fieldIndex) => ({
      key: `${lineKey}-${change.field}`,
      item,
      fieldIndex
    }));
  });

export const VersionChanges: React.FC<Props> = ({
  contractId,
  versionId,
  isInitialTerms,
  currency
}) => {
  const { t } = useTranslation();
  // `useId` rather than a hand-built key: several entries can be expanded at
  // once, so the id has to be unique per mount, and this is the convention the
  // repo already uses for aria associations.
  const titleId = React.useId();
  const { data, isLoading, isError } = useTermsVersionChanges(
    contractId,
    versionId,
    { enabled: !isInitialTerms }
  );

  if (isInitialTerms) {
    return (
      <div className="px-3 py-3">
        <Text variant="smBodySecondary">
          {t("contracts.termsHistory.initialNoComparison")}
        </Text>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-3 py-3">
        <Text variant="smBodySecondary" className="text-error">
          {t("contracts.termsHistory.changesError")}
        </Text>
      </div>
    );
  }

  // `changeSet` is declared non-optional, but hooks.ts casts this payload from
  // `any` with no runtime validation — so the type is a claim about the wire,
  // not a guarantee. A response without it would throw here mid-render.
  const changed = (data?.changeSet?.items ?? []).filter(isChangedItem);
  const rows = buildRows(changed);

  if (rows.length === 0) {
    return (
      <div className="px-3 py-3">
        <Text variant="smBodySecondary">
          {t("contracts.termsHistory.noChanges")}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-test="version-changes">
      <div className="px-3 pt-3 pb-2">
        <Text variant="body" id={titleId}>
          {t("contracts.termsHistory.whatChanged")}
        </Text>
      </div>
      {/* The visible title is the table's accessible name, so a screen-reader
          user landing on the table directly still gets it. */}
      <table className="w-full" aria-labelledby={titleId}>
        <thead>
          <tr className="border-b border-border-border1">
            {[
              "contracts.termsHistory.colService",
              "contracts.termsHistory.colField",
              "contracts.termsHistory.colBefore"
            ].map((key) => (
              <th key={key} className="px-3 py-2 text-left font-normal">
                <Text variant="smBodySecondary">{t(key)}</Text>
              </th>
            ))}
            {/* The arrow has a column of its own so it sits clear of both
                values instead of crowding the After one. */}
            <th className="w-8" aria-hidden />
            <th className="px-3 py-2 text-left font-normal">
              <Text variant="smBodySecondary">
                {t("contracts.termsHistory.colAfter")}
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, item, fieldIndex }) => {
            // A whole-line row stands in for an added or deleted service, which
            // reports no field changes: its own total is the before/after pair.
            const change =
              fieldIndex === null ? null : item.changedFields[fieldIndex];
            const isWholeLine = change === null;
            return (
              <tr
                key={key}
                className="border-b border-border-border1 last:border-b-0"
              >
                <td className="px-3 py-2 align-top max-w-[240px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <IconContainer size="sm" color="secondary">
                      <Product />
                    </IconContainer>
                    {/* min-w-0 on the item, not just the container: a flex
                        item's `min-width: auto` is what blocks shrinking, so
                        `truncate` never clips without it. The cell is bounded
                        too — under auto table-layout a nowrap column otherwise
                        just grows to fit the whole name. */}
                    <Text variant="body" className="truncate min-w-0">
                      {item.serviceItemName ?? "—"}
                    </Text>
                    {/* The status belongs to the line, so it shows on its first row. */}
                    {fieldIndex === 0 || isWholeLine ? (
                      <Text
                        variant="smBody"
                        className={cn(
                          "rounded-md px-1.5 py-0.5 w-fit shrink-0",
                          STATUS_CLASSES[item.status]
                        )}
                      >
                        {t(STATUS_KEYS[item.status])}
                      </Text>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  <Text variant="body">
                    {isWholeLine ? t("contractTerms.total") : change.label}
                  </Text>
                </td>
                <td className="px-3 py-2 align-top">
                  <Text variant="bodySecondary">
                    {isWholeLine
                      ? formatCurrency(item.totalBefore, currency)
                      : changeValueText(
                          change,
                          "before",
                          item.changedFields,
                          currency,
                          t
                        )}
                  </Text>
                </td>
                <td className="w-8 align-top py-2">
                  <IconContainer size="sm" color="secondary">
                    <ArrowRight />
                  </IconContainer>
                </td>
                <td className="px-3 py-2 align-top">
                  <Text variant="body">
                    {isWholeLine
                      ? formatCurrency(item.totalAfter, currency)
                      : changeValueText(
                          change,
                          "after",
                          item.changedFields,
                          currency,
                          t
                        )}
                  </Text>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
